import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonDropdown,
  CopyToClipboard,
  Header,
  Icon,
  Modal,
  PropertyFilterProps,
  SegmentedControl,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Tabs,
} from "@amzn/awsui-components-react";
import { CodeView } from "@amzn/awsui-code-view";
import {
  ClassificationEntry,
  ComparisonEnvironment,
  FinancialAmountRow,
} from "../../lusca-search-api/generated-src";
import { downloadShadowTestCSV } from "../../helpers/csvExportUtils";
import {
  getShadowTestResultStatus,
  ShadowTestResultResponseWithCompat,
  getShadowTestStatusIndicatorType,
} from "../../helpers/shadowTestResultStatus";
import {
  DiscrepancyTable,
  LineWithStatus,
  LinesTab,
  MatchSetTable,
  SummaryTab,
  withStatus,
} from "./ShadowTestResultTabs";
import { setModalParam } from "../../helpers/modalUrlUtils";
import {
  comparisonEnvironmentLabel,
  comparisonEnvironmentStageName,
} from "../../helpers/shadowTestComparisonEnvironment";

interface ShadowTestResultModalProps {
  visible: boolean;
  onDismiss: () => void;
  result: ShadowTestResultResponseWithCompat | null | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  isAwaitingResult?: boolean;
  error: Error | null;
  onRefresh: () => void;
  cliCommand: string;
  classifications: Record<string, ClassificationEntry>;
  comparisonEnvironment: ComparisonEnvironment;
  availableComparisonEnvironments: ComparisonEnvironment[];
  onComparisonEnvironmentChange: (environment: ComparisonEnvironment) => void;
  onTriggerTest: (comparisonEnvironment?: ComparisonEnvironment) => void;
  isTriggerLoading: boolean;
  triggerError: Error | null;
  canTrigger: boolean;
  /** Tab to restore on open (id from the tabs array, e.g. "lines"). */
  initialTab?: string;
  /** Quick-filter to restore on the Lines tab (e.g. "failing"). */
  initialFilter?: string;
  /** PropertyFilter query to restore on the Lines tab. */
  initialPropertyFilter?: PropertyFilterProps.Query;
}

const buildShadowTestUrlExtras = (
  comparisonEnvironment: ComparisonEnvironment,
  tabId: string | undefined,
  filter: string,
  pf?: PropertyFilterProps.Query
): Record<string, string> => {
  const extras: Record<string, string> = { comparisonEnvironment };
  if (tabId) extras.stTab = tabId;
  if (filter && filter !== "all") extras.stFilter = filter;
  if (pf && pf.tokens.length > 0) extras.stQ = JSON.stringify(pf);
  return extras;
};

export default function ShadowTestResultModal({
  visible,
  onDismiss,
  result,
  isLoading,
  isRefreshing,
  isAwaitingResult = false,
  error,
  onRefresh,
  cliCommand,
  classifications,
  comparisonEnvironment,
  availableComparisonEnvironments,
  onComparisonEnvironmentChange,
  onTriggerTest,
  isTriggerLoading,
  triggerError,
  canTrigger,
  initialTab,
  initialFilter,
  initialPropertyFilter,
}: ShadowTestResultModalProps) {
  const comparisonLabel = comparisonEnvironmentLabel(comparisonEnvironment);
  const comparisonStageName = comparisonEnvironmentStageName(
    comparisonEnvironment
  );
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [triggerFailed, setTriggerFailed] = useState(false);
  const wasTriggerLoadingRef = useRef(false);
  // Controlled tab state. Starts as the URL-restored tab (or undefined so Cloudscape
  // defaults to the first available tab before the user has navigated).
  const [activeTabId, setActiveTabId] = useState<string | undefined>(
    initialTab || undefined
  );

  // Refs are always up-to-date regardless of which render created a closure, so
  // all URL-sync callbacks remain correct even if Cloudscape Tabs caches content.
  const activeTabIdRef = useRef<string | undefined>(initialTab || undefined);
  activeTabIdRef.current = activeTabId; // keep in sync with state on every render

  const validInitialFilter = ["all", "matched", "unique", "failing"].includes(
    initialFilter ?? ""
  )
    ? (initialFilter as string)
    : "all";
  const activeFilterRef = useRef<string>(validInitialFilter);

  // Per-tab PropertyFilter queries — kept in memory so switching back restores the filter.
  const linesFilterRef = useRef<PropertyFilterProps.Query | undefined>(
    initialTab === "lines" ? initialPropertyFilter : undefined
  );
  const discFilterRef = useRef<PropertyFilterProps.Query | undefined>(
    initialTab === "discrepancies" ? initialPropertyFilter : undefined
  );
  const matchSetFilterRef = useRef<PropertyFilterProps.Query | undefined>(
    initialTab === "match-sets" ? initialPropertyFilter : undefined
  );

  /** Returns the filter ref for the given tab id. */
  const tabFilterRef = (tabId: string | undefined) => {
    if (tabId === "lines") return linesFilterRef;
    if (tabId === "discrepancies") return discFilterRef;
    if (tabId === "match-sets") return matchSetFilterRef;
    return { current: undefined } as React.MutableRefObject<
      PropertyFilterProps.Query | undefined
    >;
  };

  useEffect(() => {
    if (!visible) {
      setTriggerSuccess(false);
      setTriggerFailed(false);
      wasTriggerLoadingRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const tabId = activeTabIdRef.current;
    const propertyFilter =
      tabId === "lines"
        ? linesFilterRef.current
        : tabId === "discrepancies"
        ? discFilterRef.current
        : tabId === "match-sets"
        ? matchSetFilterRef.current
        : undefined;
    setModalParam(
      "shadow-test",
      buildShadowTestUrlExtras(
        comparisonEnvironment,
        tabId,
        activeFilterRef.current,
        propertyFilter
      )
    );
  }, [comparisonEnvironment, visible]);

  useEffect(() => {
    setTriggerSuccess(false);
    setTriggerFailed(false);
  }, [result]);

  useEffect(() => {
    if (isTriggerLoading) {
      wasTriggerLoadingRef.current = true;
      setTriggerSuccess(false);
      setTriggerFailed(false);
    } else if (wasTriggerLoadingRef.current) {
      wasTriggerLoadingRef.current = false;
      if (triggerError) {
        setTriggerFailed(true);
      } else {
        setTriggerSuccess(true);
      }
    }
  }, [isTriggerLoading, triggerError]);

  const isTriggerDisabled = !canTrigger || isAwaitingResult;

  const triggerButton = (text: string, primary = false) => (
    <ButtonDropdown
      variant={primary ? "primary" : "normal"}
      ariaLabel={`${text} options`}
      mainAction={{
        text,
        onClick: () => onTriggerTest(),
        loading: isTriggerLoading,
        disabled: isTriggerDisabled,
      }}
      items={[
        {
          id: "beta",
          text: "Run against Beta",
          disabled: isTriggerDisabled || isTriggerLoading,
        },
      ]}
      onItemClick={({ detail }) => {
        if (detail.id === "beta") {
          onTriggerTest(ComparisonEnvironment.Beta);
        }
      }}
    />
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      );
    }

    if (error) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l">
            <StatusIndicator type="error">
              {error.message || "An unexpected error occurred."}
            </StatusIndicator>
          </Box>
          <Box textAlign="center">
            <Button iconName="refresh" onClick={onRefresh}>
              Retry
            </Button>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    if (!result) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l" color="text-status-inactive">
            No shadow test results found for this transaction.
          </Box>
          <Box textAlign="center">
            <SpaceBetween size="xs">
              {triggerButton("Run Shadow Test", true)}
              {triggerSuccess && (
                <StatusIndicator type="success">Test triggered</StatusIndicator>
              )}
              {triggerFailed && (
                <StatusIndicator type="error">Test failed</StatusIndicator>
              )}
            </SpaceBetween>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    const testDate = result.testTimestamp
      ? new Date(result.testTimestamp).toLocaleString()
      : "";
    const resultStatus = getShadowTestResultStatus(result);
    const resultStatusMessage = result.resultStatusMessage?.trim();

    const tabs: { id: string; label: string; content: JSX.Element }[] = [];

    // Summary tab leads: a compact overview (currently the usecase presence matrix) that keeps the
    // detail out of the always-visible header. Room to grow into a checks rollup (financial
    // validation, amount check, bundle integrity, ...).
    const hasAnyUsecases =
      (result.gammaUsecases?.length ?? 0) +
        (result.prodUsecases?.length ?? 0) +
        (result.jemUsecases?.length ?? 0) >
      0;
    // Overall pass/fail already shows in the header strip, so a Summary tab is only worth adding when
    // there is more to summarise: usecases, or a validation check the backend actually ran.
    const hasSummary =
      hasAnyUsecases ||
      result.sourceAmountCheckPassed != null ||
      result.bundleAllocationPassed != null ||
      result.bundleAllocationPaymentIntegrityPassed != null;
    if (hasSummary) {
      tabs.push({
        id: "summary",
        label: "Summary",
        content: (
          <SummaryTab
            result={result}
            comparisonEnvironment={comparisonEnvironment}
          />
        ),
      });
    }

    // Matched and unique lines share one "Lines" tab. Every line is tagged matched/unique internally
    // so the quick-filter segmented control can split them, but there is no dedicated status column
    // — the Environment column already shows source, and the Classification column is the only
    // correctness signal. Default sort (usecase then GL line description) puts related matched and
    // unique rows next to each other, the comparison that used to require flipping tabs.
    const allLines: LineWithStatus[] = [
      ...withStatus(
        result.matchedLines,
        `${comparisonStageName}+prod`,
        "Matched",
        comparisonEnvironment
      ),
      ...withStatus(
        result.gammaUniqueLines,
        comparisonStageName,
        `${comparisonLabel} only`,
        comparisonEnvironment
      ),
      ...withStatus(
        result.prodUniqueLines,
        "prod",
        "Prod only",
        comparisonEnvironment
      ),
      ...withStatus(
        result.jemUniqueLines,
        "jem",
        "JEM only",
        comparisonEnvironment
      ),
    ];

    if (allLines.length > 0) {
      tabs.push({
        id: "lines",
        label: `Lines (${allLines.length})`,
        content: (
          <LinesTab
            isBundle={result.isBundle}
            lines={allLines}
            classifications={classifications}
            initialFilter={validInitialFilter}
            onFilterChange={(filter) => {
              activeFilterRef.current = filter;
              setModalParam(
                "shadow-test",
                buildShadowTestUrlExtras(
                  comparisonEnvironment,
                  activeTabIdRef.current,
                  filter,
                  linesFilterRef.current
                )
              );
            }}
            initialPropertyFilter={linesFilterRef.current}
            onPropertyFilterChange={(query) => {
              linesFilterRef.current = query;
              setModalParam(
                "shadow-test",
                buildShadowTestUrlExtras(
                  comparisonEnvironment,
                  activeTabIdRef.current,
                  activeFilterRef.current,
                  query
                )
              );
            }}
          />
        ),
      });
    }

    if (result.discrepancies && result.discrepancies.length > 0) {
      tabs.push({
        id: "discrepancies",
        label: `Discrepancies (${result.discrepancies.length})`,
        content: (
          <DiscrepancyTable
            isBundle={result.isBundle}
            discrepancies={result.discrepancies}
            classifications={classifications}
            comparisonLabel={comparisonLabel}
            initialPropertyFilter={discFilterRef.current}
            onPropertyFilterChange={(query) => {
              discFilterRef.current = query;
              setModalParam(
                "shadow-test",
                buildShadowTestUrlExtras(
                  comparisonEnvironment,
                  activeTabIdRef.current,
                  activeFilterRef.current,
                  query
                )
              );
            }}
          />
        ),
      });
    }

    if (result.matchSets && result.matchSets.length > 0) {
      tabs.push({
        id: "match-sets",
        label: `Match Sets (${result.matchSets.length})`,
        content: (
          <MatchSetTable
            matchSets={result.matchSets}
            countryCode={result.countryCode}
            classifications={classifications}
            comparisonEnvironment={comparisonEnvironment}
            initialPropertyFilter={matchSetFilterRef.current}
            onPropertyFilterChange={(query) => {
              matchSetFilterRef.current = query;
              setModalParam(
                "shadow-test",
                buildShadowTestUrlExtras(
                  comparisonEnvironment,
                  activeTabIdRef.current,
                  activeFilterRef.current,
                  query
                )
              );
            }}
          />
        ),
      });
    }

    if (
      result.gammaPivot ||
      result.prodPivot ||
      result.financialAmounts ||
      result.bundleAllocation
    ) {
      tabs.push({
        id: "account-balance",
        label: "Financial Validation",
        content: (
          <SpaceBetween size="l">
            {result.financialAmounts &&
              Object.keys(result.financialAmounts).length > 0 && (
                <SpaceBetween size="xs">
                  <Box variant="h4">Financial Amounts</Box>
                  <FinancialAmountsTable
                    rows={result.financialAmounts}
                    comparisonLabel={comparisonLabel}
                  />
                </SpaceBetween>
              )}
            {result.bundleAllocation &&
              Object.keys(result.bundleAllocation).length > 0 && (
                <SpaceBetween size="xs">
                  <BundleAllocationTables
                    rows={result.bundleAllocation}
                    paymentIntegrityPassed={
                      result.bundleAllocationPaymentIntegrityPassed
                    }
                  />
                </SpaceBetween>
              )}
            {result.gammaPivot && Object.keys(result.gammaPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">
                  Account Balance Pivot - {comparisonLabel}
                </Box>
                <PivotTable
                  pivot={result.gammaPivot}
                  pivotByProduct={result.gammaPivotByProduct}
                />
              </SpaceBetween>
            )}
            {result.prodPivot && Object.keys(result.prodPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">Account Balance Pivot - Prod</Box>
                <PivotTable
                  pivot={result.prodPivot}
                  pivotByProduct={result.prodPivotByProduct}
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        ),
      });
    }

    return (
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal">
          <StatusIndicator
            type={getShadowTestStatusIndicatorType(resultStatus)}
          >
            {resultStatus}
          </StatusIndicator>
          {result.sourceAmountCheckPassed != null && (
            <>
              <Box color="text-body-secondary">·</Box>
              <StatusIndicator
                type={result.sourceAmountCheckPassed ? "success" : "error"}
              >
                {result.sourceAmountCheckPassed
                  ? "Amounts Match"
                  : "Amount Mismatch"}
              </StatusIndicator>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box>GL {result.glProductGroup}</Box>
          {resultStatusMessage && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box data-testid="result-status-message">
                {resultStatusMessage}
              </Box>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box>{result.countryCode}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.paymentProvider || "-"}</Box>
          {result.isPromo && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box color="text-status-warning">Promo</Box>
            </>
          )}
          {result.isBundle && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box>Bundle</Box>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box color="text-body-secondary">Tested {testDate}</Box>
        </SpaceBetween>

        {tabs.length > 0 && (
          <Tabs
            tabs={tabs}
            activeTabId={
              activeTabId && tabs.some((t) => t.id === activeTabId)
                ? activeTabId
                : undefined
            }
            onChange={({ detail }) => {
              setActiveTabId(detail.activeTabId);
              activeTabIdRef.current = detail.activeTabId;
              setModalParam(
                "shadow-test",
                buildShadowTestUrlExtras(
                  comparisonEnvironment,
                  detail.activeTabId,
                  activeFilterRef.current,
                  tabFilterRef(detail.activeTabId).current
                )
              );
            }}
          />
        )}

        <CliCommandSection cliCommand={cliCommand} />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      header={
        <Header
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              {availableComparisonEnvironments.length === 2 && (
                <SegmentedControl
                  selectedId={comparisonEnvironment}
                  onChange={({ detail }) =>
                    onComparisonEnvironmentChange(
                      detail.selectedId as ComparisonEnvironment
                    )
                  }
                  label="Shadow test comparison environment"
                  options={[
                    { id: ComparisonEnvironment.Gamma, text: "Gamma" },
                    { id: ComparisonEnvironment.Beta, text: "Beta" },
                  ]}
                />
              )}
              <Button
                iconName="refresh"
                onClick={onRefresh}
                loading={isRefreshing || isAwaitingResult}
              >
                Refresh
              </Button>
              {result && (
                <>
                  <Button
                    iconName="download"
                    onClick={() =>
                      downloadShadowTestCSV(
                        result,
                        classifications,
                        comparisonEnvironment
                      )
                    }
                  >
                    Export CSV
                  </Button>
                  {triggerButton("Re-run Test")}
                  {triggerSuccess && (
                    <StatusIndicator type="success">
                      Test triggered
                    </StatusIndicator>
                  )}
                  {triggerFailed && (
                    <StatusIndicator type="error">Test failed</StatusIndicator>
                  )}
                </>
              )}
            </SpaceBetween>
          }
        >
          Shadow Test Result
        </Header>
      }
    >
      {renderContent()}
    </Modal>
  );
}

const formatLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Decide whether a source amount should be flagged as mismatched against a USL amount for the
// Financial Validation tab. When currencies differ we cannot compare on the client (no FX rate
// available here), so suppress row-level red and defer to the backend-computed tab pass/fail.
// When currencies match, compare the string amounts directly (same as before).
const amountMismatch = (
  sourceAmount: string | undefined,
  sourceCurr: string | undefined,
  uslAmount: string | undefined,
  uslCurr: string | undefined
): boolean => {
  if (!sourceAmount || !uslAmount) return false;
  if (sourceCurr && uslCurr && sourceCurr !== uslCurr) return false;
  return sourceAmount !== uslAmount;
};

// Visually hidden but readable by screen readers / assistive tech.
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const BUNDLE_ALLOCATION_TOTAL_LABEL = "Total Bundle Level";

// Keys must match the cell suffixes the shadow test writes to BUNDLE_ALLOCATION.
const BUNDLE_ALLOCATION_METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "royalty", label: "Royalty" },
  { key: "tax", label: "Tax" },
  { key: "psaa_cash_receiving", label: "PSAA Cash Receiving" },
  { key: "psaa_stored_value", label: "PSAA Stored Value" },
  { key: "promo", label: "Promo" },
];

interface BundleAllocationRow {
  component: string;
  cells: { [key: string]: string };
  isTotal: boolean;
}

/**
 * Bundle split validation as a single table. Each metric column is split into
 * two aligned halves — the pro-rata recalc (what each component should have
 * been booked) and the USL actual (what Lusca actually booked) — separated by
 * a divider. The per-metric match verdict is folded onto the USL half: a green
 * value + tick when it reconciles with the recalc, red + cross when it does
 * not, so no separate verdict table is needed.
 *
 * This Cloudscape version has no spanning column header, so the two halves are
 * laid out with flex inside one column rather than as true sub-columns.
 */
const BundleAllocationTables = ({
  rows,
  paymentIntegrityPassed,
}: {
  rows: { [key: string]: { [key: string]: string } };
  paymentIntegrityPassed?: boolean;
}) => {
  const items: BundleAllocationRow[] = Object.entries(rows)
    .filter(([component]) => component !== BUNDLE_ALLOCATION_TOTAL_LABEL)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([component, cells]) => ({ component, cells, isTotal: false }));

  const totals = rows[BUNDLE_ALLOCATION_TOTAL_LABEL];
  if (totals) {
    items.push({
      component: BUNDLE_ALLOCATION_TOTAL_LABEL,
      cells: totals,
      isTotal: true,
    });
  }
  if (items.length === 0) return null;

  const metrics = BUNDLE_ALLOCATION_METRICS;

  const value = (row: BundleAllocationRow, cellKey: string) =>
    row.cells[cellKey] ?? "n/a";

  const componentColumn = (header: string) => ({
    id: "component",
    header,
    cell: (e: BundleAllocationRow) => (
      <Box fontWeight={e.isTotal ? "bold" : "normal"}>{e.component}</Box>
    ),
    minWidth: 280,
  });

  const sspColumn = {
    id: "ssp",
    header: "SSP",
    cell: (e: BundleAllocationRow) => (
      <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
        {value(e, "ssp")}
      </Box>
    ),
  };

  const allocationPercentColumn = {
    id: "allocationPercent",
    header: "Allocation %",
    cell: (e: BundleAllocationRow) => (
      <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
        {e.isTotal
          ? "100.00%"
          : e.cells.allocation_percent !== undefined
          ? `${e.cells.allocation_percent}%`
          : "n/a"}
      </Box>
    ),
  };

  // Each metric column splits into a Recalc half and a USL half, separated by a
  // divider. Header and cells share the same flex structure so they stay aligned.
  const splitRow: React.CSSProperties = { display: "flex" };
  const leftHalf: React.CSSProperties = {
    flex: 1,
    textAlign: "center",
    paddingRight: "8px",
    borderRight: "1px solid var(--color-border-divider-default, #d1d5db)",
  };
  const rightHalf: React.CSSProperties = {
    flex: 1,
    textAlign: "center",
    paddingLeft: "8px",
  };
  const metricColumns = metrics.map((metric) => ({
    id: metric.key,
    header: (
      <div style={{ textAlign: "center" }}>
        <div>{metric.label}</div>
        <div style={splitRow}>
          <span style={leftHalf}>
            <Box variant="span" fontSize="body-s" color="text-body-secondary">
              Recalc
            </Box>
          </span>
          <span style={rightHalf}>
            <Box variant="span" fontSize="body-s" color="text-body-secondary">
              USL
            </Box>
          </span>
        </div>
      </div>
    ),
    cell: (e: BundleAllocationRow) => {
      const proRata = e.cells[`pro_rata_${metric.key}`];
      const uslValue = e.cells[`usl_${metric.key}`];
      const bold = e.isTotal ? "bold" : "normal";
      const match = e.cells[`match_${metric.key}`];
      const passed = match === "true";
      // Colour + tick/cross only when there is both a booked value and a verdict.
      const uslColor =
        uslValue === undefined || match === undefined
          ? undefined
          : passed
          ? "text-status-success"
          : "text-status-error";
      return (
        <div style={splitRow}>
          <span style={leftHalf}>
            <Box variant="span" fontWeight={bold} color="text-body-secondary">
              {proRata ?? "n/a"}
            </Box>
          </span>
          <span style={rightHalf}>
            <Box variant="span" fontWeight={bold} color={uslColor}>
              {uslValue === undefined ? (
                "n/a"
              ) : uslColor === undefined ? (
                uslValue
              ) : (
                <>
                  <Icon
                    name={passed ? "status-positive" : "status-negative"}
                    variant={passed ? "success" : "error"}
                  />
                  {/* Screen-reader verdict so the match isn't conveyed by colour alone. */}
                  <span style={SR_ONLY}>
                    {passed ? "matched:" : "mismatch:"}
                  </span>{" "}
                  {uslValue}
                </>
              )}
            </Box>
          </span>
        </div>
      );
    },
  }));

  // Payment integrity compares the order payment total against the summed
  // component SSPs. Both numbers live on the Total row (summed SSP is its "ssp"
  // cell; payment_total rides the same cell map when the backend provides it),
  // so the check is shown with its inputs rather than as a bare verdict.
  const summedSsp = totals?.ssp;
  const paymentTotal = totals?.payment_total;
  const paymentIntegrityLabel = () => {
    const passed = paymentIntegrityPassed;
    const verdict = passed
      ? "Payment total matches summed SSPs"
      : "Payment total does not match summed SSPs";
    if (paymentTotal !== undefined && summedSsp !== undefined) {
      return `${verdict} (payment ${paymentTotal} vs summed SSPs ${summedSsp})`;
    }
    if (summedSsp !== undefined) {
      return `${verdict} (summed SSPs ${summedSsp})`;
    }
    return verdict;
  };

  return (
    <SpaceBetween size="xs">
      <Box variant="h4">Bundle Allocation</Box>
      {paymentIntegrityPassed !== undefined && (
        <StatusIndicator type={paymentIntegrityPassed ? "success" : "error"}>
          {paymentIntegrityLabel()}
        </StatusIndicator>
      )}
      <Box variant="small" color="text-body-secondary">
        Each metric splits into the pro-rata recalc and the USL actual; the USL
        value is marked ✓ when it reconciles, ✗ when it does not.
      </Box>
      <div style={{ fontSize: "12px", overflowX: "auto" }}>
        <Table
          items={items}
          columnDefinitions={[
            componentColumn("Bundle Content"),
            sspColumn,
            allocationPercentColumn,
            ...metricColumns,
          ]}
          variant="embedded"
        />
      </div>
    </SpaceBetween>
  );
};

const FinancialAmountsTable = ({
  rows,
  comparisonLabel,
}: {
  rows: { [key: string]: FinancialAmountRow };
  comparisonLabel: string;
}) => (
  <div style={{ fontSize: "12px", overflowX: "auto", width: "100%" }}>
    <Table
      items={Object.entries(rows).map(([key, row]) => ({ key, ...row }))}
      columnDefinitions={[
        {
          id: "element",
          header: "Element",
          cell: (e) => formatLabel(e.key),
          minWidth: 220,
        },
        {
          id: "source",
          header: "Source",
          cell: (e) => e.source || "-",
          minWidth: 140,
        },
        {
          id: "sourceAmount",
          header: "Source Amount",
          cell: (e) => e.sourceAmount || "-",
          minWidth: 140,
        },
        {
          id: "sourceCurr",
          header: "Source Curr",
          cell: (e) => e.sourceCurr || "-",
          minWidth: 120,
        },
        {
          id: "gammaUslAmount",
          header: `${comparisonLabel} USL Amount`,
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.gammaUslAmount,
              e.gammaUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.gammaUslAmount || "-"}
              </Box>
            );
          },
          minWidth: 160,
        },
        {
          id: "gammaUslCurr",
          header: `${comparisonLabel} USL Curr`,
          cell: (e) => <Box>{e.gammaUslCurr || "-"}</Box>,
          minWidth: 140,
        },
        {
          id: "prodUslAmount",
          header: "Prod USL Amount",
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.prodUslAmount,
              e.prodUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.prodUslAmount || "-"}
              </Box>
            );
          },
          minWidth: 160,
        },
        {
          id: "prodUslCurr",
          header: "Prod USL Curr",
          cell: (e) => <Box>{e.prodUslCurr || "-"}</Box>,
          minWidth: 140,
        },
      ]}
      variant="embedded"
    />
  </div>
);

interface PivotRow {
  id: string;
  glDesc: string;
  cells: { [key: string]: number };
  rowTotal: number;
  isTotal: boolean;
  children: PivotRow[];
}

const PivotTable = ({
  pivot,
  pivotByProduct,
}: {
  pivot: { [key: string]: { [key: string]: number } };
  pivotByProduct?: {
    [key: string]: { [key: string]: { [key: string]: number } };
  };
}) => {
  const [expandedItems, setExpandedItems] = useState<PivotRow[]>([]);

  const accounts = useMemo(
    () =>
      Array.from(
        new Set(Object.values(pivot).flatMap((row) => Object.keys(row)))
      ).sort(),
    [pivot]
  );

  const items = useMemo<PivotRow[]>(() => {
    const rows = Object.entries(pivot).sort(([a], [b]) => a.localeCompare(b));
    const total = (cells: { [key: string]: number }) =>
      accounts.reduce((sum, account) => sum + (cells[account] ?? 0), 0);

    const colTotals: Record<string, number> = {};
    accounts.forEach((account) => (colTotals[account] = 0));
    rows.forEach(([, cells]) =>
      accounts.forEach(
        (account) =>
          (colTotals[account] =
            (colTotals[account] ?? 0) + (cells[account] ?? 0))
      )
    );

    const componentRows = (glDesc: string): PivotRow[] =>
      Object.entries(pivotByProduct?.[glDesc] ?? {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([gti, cells]) => ({
          id: `${glDesc}-${gti}`,
          glDesc: gti,
          cells,
          rowTotal: total(cells),
          isTotal: false,
          children: [],
        }));

    return [
      ...rows.map(([glDesc, cells]) => ({
        id: glDesc,
        glDesc,
        cells,
        rowTotal: total(cells),
        isTotal: false,
        children: componentRows(glDesc),
      })),
      {
        id: "grand-total",
        glDesc: "Grand Total",
        cells: colTotals,
        rowTotal: accounts.reduce(
          (sum, account) => sum + colTotals[account],
          0
        ),
        isTotal: true,
        children: [],
      },
    ];
  }, [pivot, pivotByProduct, accounts]);

  const hasComponents = items.some((item) => item.children.length > 0);

  const fmt = (n: number) => (n === 0 ? "0" : n.toFixed(2));

  return (
    <div style={{ fontSize: "12px", overflowX: "auto" }}>
      <Table
        items={items}
        trackBy="id"
        columnDefinitions={[
          {
            id: "glDesc",
            header: "GL Line Description",
            cell: (e) => (
              <Box fontWeight={e.isTotal ? "bold" : "normal"}>{e.glDesc}</Box>
            ),
            minWidth: 280,
          },
          ...accounts.map((a) => ({
            id: a,
            header: a,
            cell: (e: PivotRow) => (
              <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
                {fmt(e.cells[a] ?? 0)}
              </Box>
            ),
          })),
          {
            id: "grandTotal",
            header: "Grand Total",
            // Red flags an unbalanced total only on the Grand Total row, where a
            // nonzero sum means debits and credits don't net out. Per-GL-line and
            // per-component (GTI) rows legitimately have nonzero totals, so they
            // are never coloured.
            cell: (e) => (
              <Box
                textAlign="right"
                fontWeight={e.isTotal ? "bold" : "normal"}
                color={
                  e.isTotal && e.rowTotal !== 0
                    ? "text-status-error"
                    : undefined
                }
              >
                {fmt(e.rowTotal)}
              </Box>
            ),
          },
        ]}
        variant="embedded"
        expandableRows={
          hasComponents
            ? {
                getItemChildren: (item) => item.children,
                isItemExpandable: (item) => item.children.length > 0,
                expandedItems,
                onExpandableItemToggle: ({ detail }) =>
                  setExpandedItems((currentItems) =>
                    detail.expanded
                      ? [...currentItems, detail.item]
                      : currentItems.filter(
                          (item) => item.id !== detail.item.id
                        )
                  ),
                getItemsCount: (item) => item.children.length,
              }
            : undefined
        }
        ariaLabels={
          hasComponents
            ? {
                expandButtonLabel: (item) => `Expand ${item.glDesc}`,
                collapseButtonLabel: (item) => `Collapse ${item.glDesc}`,
              }
            : undefined
        }
      />
    </div>
  );
};

const CliCommandSection = ({ cliCommand }: { cliCommand: string }) => (
  <Box>
    <Box variant="h4">Run locally</Box>
    <CodeView
      content={cliCommand}
      actions={
        <CopyToClipboard
          copyButtonAriaLabel="Copy command"
          copyErrorText="Failed to copy"
          copySuccessText="Copied"
          textToCopy={cliCommand}
        />
      }
    />
  </Box>
);
